/**
 * 광고주가 캠페인에 어울리는 크리에이터를 추천받을 때 쓰는 순수 정렬 로직.
 * (docs/vibe-matching-design.md 4장, 결정 (a) 2차 정렬)
 *
 * 이 파일은 의도적으로 Nuxt 자동 임포트/Pinia/Firebase에 의존하지 않는 순수 함수만
 * 담는다 — `app/composables/campaign.ts` 같은 파일은 최상단에서 `~~/firebaseConfig`
 * 같은 Nuxt 별칭을 정적으로 import하기 때문에 Nuxt 런타임 밖(plain `node --test`)에서
 * 그대로 불러올 수 없다. 반대로 이 파일은 어디서든(테스트 포함) 그냥 import해서 쓸 수 있다.
 *
 * 키워드 유사도(`calculateKeywordSimilarity`, app/composables/campaign.ts) 계산 자체는
 * 이 모듈의 책임이 아니다 — 호출부가 이미 계산해서 각 후보 객체에 `keywordSimilarity`로
 * 채워 넣은 상태로 넘겨준다고 가정한다. 이 모듈은 "이미 계산된 두 점수를 어떤 순서로
 * 합쳐 정렬할지"만 책임진다.
 */

/** 정렬 대상 후보. 원본 크리에이터 데이터에 keywordSimilarity가 이미 채워져 있어야 한다. */
export interface CreatorMatchCandidate {
  id: string
  keywordSimilarity: number
  [key: string]: unknown
}

/** creatorRecommendationScores/{channelId} 프로젝션 문서의 client-safe 형태. */
export interface CreatorScoreProjection {
  finalScore: number
  calculatedAt?: unknown
}

export interface RankedCreatorMatch<T extends CreatorMatchCandidate = CreatorMatchCandidate> {
  creator: T
  keywordSimilarity: number
  /** vibe 점수가 없으면(아직 채점 안 됨/실패) null — 0이나 50 같은 기본값으로 채우지 않는다. */
  finalScore: number | null
}

/**
 * 크리에이터 후보 목록을 키워드 유사도(1차) → vibe finalScore(2차) 순으로 정렬한다.
 *
 * - 1차: keywordSimilarity 내림차순 (이미 검증된 기존 매칭 신호를 그대로 우선)
 * - 2차: 1차가 동점인 그룹 안에서만 finalScore 내림차순 — 동점이 아니면 finalScore는
 *   정렬 순서에 전혀 영향을 주지 않는다
 * - finalScore가 없는(null) 크리에이터는 목록에서 배제하지 않는다. 동점 그룹 안에서는
 *   점수가 있는 크리에이터보다 뒤로 밀리지만, 여전히 목록에 포함된다
 *
 * @param candidates keywordSimilarity가 이미 채워진 크리에이터 후보 목록
 * @param scoresByCreatorId creatorId → creatorRecommendationScores 프로젝션. 값이 없으면
 *   해당 크리에이터의 finalScore는 null로 처리된다(기본값을 임의로 채우지 않음)
 * @returns keywordSimilarity/finalScore가 함께 계산된 정렬된 목록 (입력 배열은 변경하지 않음)
 */
export function rankCreatorsByKeywordThenVibeScore<T extends CreatorMatchCandidate>(
  candidates: T[],
  scoresByCreatorId: Record<string, CreatorScoreProjection | null | undefined>,
): RankedCreatorMatch<T>[] {
  const ranked = candidates.map((creator): RankedCreatorMatch<T> => ({
    creator,
    keywordSimilarity: creator.keywordSimilarity,
    finalScore: scoresByCreatorId[creator.id]?.finalScore ?? null,
  }))

  ranked.sort((a, b) => {
    if (b.keywordSimilarity !== a.keywordSimilarity)
      return b.keywordSimilarity - a.keywordSimilarity

    // 여기 도달했다는 건 키워드 유사도가 동점이라는 뜻 — 이때만 vibe 점수로 갈린다.
    if (a.finalScore === null && b.finalScore === null)
      return 0
    if (a.finalScore === null)
      return 1
    if (b.finalScore === null)
      return -1
    return b.finalScore - a.finalScore
  })

  return ranked
}

/**
 * 크리에이터 키워드 중 캠페인 키워드와 겹치는 것을 (크리에이터 쪽 원문 그대로, 중복 제거해서) 돌려준다.
 * 비교는 대소문자·앞뒤 공백을 무시한다(유사도 계산 calculateKeywordSimilarity와 같은 기준).
 */
export function findMatchedKeywords(creatorKeywords: string[] | undefined, campaignKeywords: string[] | undefined): string[] {
  if (!creatorKeywords?.length || !campaignKeywords?.length)
    return []
  const campaignSet = new Set(campaignKeywords.map(k => k.toLowerCase().trim()))
  const seen = new Set<string>()
  const matched: string[] = []
  for (const keyword of creatorKeywords) {
    const key = keyword.toLowerCase().trim()
    if (campaignSet.has(key) && !seen.has(key)) {
      seen.add(key)
      matched.push(keyword.trim())
    }
  }
  return matched
}

/**
 * 광고주에게 보여줄 "추천 이유" 한 줄. 근거가 있는 항목만 이어 붙이고, 없는 값을 지어내지 않는다.
 */
export function buildRecommendationReason(input: {
  matchedKeywords: string[]
  finalScore: number | null
  grade?: string | null
}): string {
  const parts: string[] = []
  const shown = input.matchedKeywords.slice(0, 3)
  if (shown.length > 0) {
    const rest = input.matchedKeywords.length - shown.length
    parts.push(`캠페인과 겹치는 키워드: ${shown.join(', ')}${rest > 0 ? ` 외 ${rest}개` : ''}`)
  }
  if (input.finalScore !== null)
    parts.push(`AI 품질 점수 ${input.finalScore}점`)
  if (input.grade)
    parts.push(`${input.grade} 등급`)
  if (parts.length === 0)
    return '캠페인과 겹치는 키워드나 AI 분석 결과가 아직 없어 후보로만 보여드려요'
  return parts.join(' · ')
}
